/**
 * Consolidated Accessibility Utilities
 * Combines functionality from accessibilityUtils.js, focusUtils.js, and contrastUtils.js
 * Provides comprehensive accessibility utilities for WCAG 2.1 AA compliance
 */

import { getContrastRatio } from './contrastUtils.js';

// ============================================================================
// SCREEN READER UTILITIES
// ============================================================================

/**
 * Announce text to screen readers using live regions
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  if (!message) return;
  
  // Create or find existing live region
  let liveRegion = document.getElementById(`live-region-${priority}`);
  
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = `live-region-${priority}`;
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className='sr-only';
    liveRegion.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;
    document.body.appendChild(liveRegion);
  }
  
  // Clear and set new message
  liveRegion.textContent = '';
  setTimeout(() => {
    liveRegion.textContent = message;
  }, 100);
  
  // Clean up after announcement
  setTimeout(() => {
    liveRegion.textContent = '';
  }, 5000);
};

/**
 * Create accessible loading announcements
 */
export const announceLoadingProgress = (progress, step, status = 'loading') => {
  const messages = {
    loading: `Loading ${step}, ${progress}% complete`,
    complete: `${step} loaded successfully`,
    error: `Error loading ${step}, please try again`
  };
  
  announceToScreenReader(messages[status] || messages.loading, 'polite');
};

/**
 * Announce form validation errors
 */
export const announceFormErrors = (errors) => {
  if (!errors || errors.length === 0) return;
  
  const errorMessage = errors.length === 1 
    ? `Form error: ${errors[0]}`
    : `Form has ${errors.length} errors: ${errors.join(', ')}`;
  
  announceToScreenReader(errorMessage, 'assertive');
};

/**
 * Announce navigation changes
 */
export const announceNavigation = (pageName, isLoading = false) => {
  const message = isLoading 
    ? `Navigating to ${pageName}`
    : `Now on ${pageName} page`;
  
  announceToScreenReader(message, 'polite');
};

// ============================================================================
// FOCUS MANAGEMENT UTILITIES
// ============================================================================

/**
 * Get accessible focus ring colors for both themes
 */
export const getFocusRingColors = () => {
  return {
    light: {
      // Light theme focus indicators - use darker colors for better contrast on light backgrounds
      primary: 'focus:ring-teal-600 focus:border-teal-600', // Darker teal for better contrast
      secondary: 'focus:ring-blue-600 focus:border-blue-600', // Darker blue focus
      error: 'focus:ring-red-600 focus:border-red-600', // Darker red focus
      success: 'focus:ring-green-600 focus:border-green-600', // Darker green focus
      warning: 'focus:ring-yellow-600 focus:border-yellow-600', // Darker yellow focus
      // Focus ring offset for light backgrounds
      offset: 'focus:ring-offset-2 focus:ring-offset-white'
    },
    dark: {
      // Dark theme focus indicators - use lighter colors for better contrast on dark backgrounds
      primary: 'focus:ring-teal-400 focus:border-teal-400', // Lighter teal for dark backgrounds
      secondary: 'focus:ring-blue-400 focus:border-blue-400', // Lighter blue focus
      error: 'focus:ring-red-400 focus:border-red-400', // Lighter red focus
      success: 'focus:ring-green-400 focus:border-green-400', // Lighter green focus
      warning: 'focus:ring-yellow-400 focus:border-yellow-400', // Lighter yellow focus
      // Focus ring offset for dark backgrounds
      offset: 'focus:ring-offset-2 focus:ring-offset-gray-900'
    }
  };
};

/**
 * Get theme-aware focus classes for interactive elements
 * @param {string} theme - Current theme ('light' or 'dark')
 * @param {string} type - Focus type ('primary', 'secondary', 'error', 'success', 'warning')
 * @param {boolean} includeOffset - Whether to include ring offset classes
 * @returns {string} Complete focus class string
 */
export const getFocusClasses = (theme, type = 'primary', includeOffset = true) => {
  const colors = getFocusRingColors();
  const themeColors = colors[theme] || colors.light;
  
  let focusClasses = `focus:outline-none focus:ring-2 ${themeColors[type]}`;
  
  if (includeOffset) {
    focusClasses += ` ${themeColors.offset}`;
  }
  
  return focusClasses;
};

/**
 * Manage focus trap for modals and dialogs
 */
export class FocusTrap {
  constructor(element) {
    this.element = element;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.previouslyFocusedElement = null;
  }

  activate() {
    this.previouslyFocusedElement = document.activeElement;
    this.updateFocusableElements();
    
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }
    
    document.addEventListener('keydown', this.handleKeyDown);
  }

  deactivate() {
    document.removeEventListener('keydown', this.handleKeyDown);
    
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }

  updateFocusableElements() {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    this.focusableElements = Array.from(
      this.element.querySelectorAll(focusableSelectors)
    ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);

    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  }

  handleKeyDown = (event) => {
    if (event.key !== 'Tab') return;

    if (event.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement?.focus();
      }
    } else {
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement?.focus();
      }
    }
  };
}

/**
 * Move focus to element with announcement
 */
export const moveFocusTo = (element, announce = true) => {
  if (!element) return;
  
  element.focus();
  
  if (announce) {
    const label = element.getAttribute('aria-label') || 
                  element.getAttribute('title') || 
                  element.textContent || 
                  'Element focused';
    announceToScreenReader(`Focused on ${label}`, 'polite');
  }
};

/**
 * Skip to main content functionality
 */
export const createSkipLink = (targetId = 'main-content') => {
  const skipLink = document.createElement('a');
  skipLink.href = `#${targetId}`;
  skipLink.textContent = 'Skip to main content';
  skipLink.className='sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:no-underline dark:text-white';
  
  skipLink.addEventListener('click', (e) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      target.scrollIntoView();
    }
  });
  
  return skipLink;
};

// ============================================================================
// CONTRAST AND COLOR UTILITIES
// ============================================================================

/**
 * Check if color combination meets WCAG contrast requirements
 * @param {string} foreground - Foreground color (hex, rgb, or named)
 * @param {string} background - Background color (hex, rgb, or named)
 * @param {string} level - WCAG level ('AA' or 'AAA')
 * @param {string} size - Text size ('normal' or 'large')
 * @returns {boolean} Whether the combination meets requirements
 */
export const meetsContrastRequirements = (foreground, background, level = 'AA', size = 'normal') => {
  const ratio = getContrastRatio(foreground, background);
  
  const requirements = {
    'AA': { normal: 4.5, large: 3 },
    'AAA': { normal: 7, large: 4.5 }
  };
  
  return ratio >= requirements[level][size];
};

/**
 * Get accessible color pairs for themes
 */
export const getAccessibleColorPairs = (theme = 'light') => {
  const pairs = {
    light: {
      primary: { text: '#1F2937', background: '#FFFFFF' }, // Gray-800 on White
      secondary: { text: '#374151', background: '#F9FAFB' }, // Gray-700 on Gray-50
      success: { text: '#065F46', background: '#D1FAE5' }, // Green-800 on Green-100
      warning: { text: '#92400E', background: '#FEF3C7' }, // Yellow-800 on Yellow-100
      error: { text: '#991B1B', background: '#FEE2E2' }, // Red-800 on Red-100
      info: { text: '#1E40AF', background: '#DBEAFE' } // Blue-800 on Blue-100
    },
    dark: {
      primary: { text: '#F9FAFB', background: '#111827' }, // Gray-50 on Gray-900
      secondary: { text: '#E5E7EB', background: '#1F2937' }, // Gray-200 on Gray-800
      success: { text: '#A7F3D0', background: '#064E3B' }, // Green-200 on Green-800
      warning: { text: '#FDE68A', background: '#78350F' }, // Yellow-200 on Yellow-800
      error: { text: '#FECACA', background: '#7F1D1D' }, // Red-200 on Red-800
      info: { text: '#BFDBFE', background: '#1E3A8A' } // Blue-200 on Blue-800
    }
  };
  
  return pairs[theme] || pairs.light;
};

// ============================================================================
// ARIA UTILITIES
// ============================================================================

/**
 * Generate ARIA attributes for interactive elements
 */
export const generateAriaAttributes = (type, options = {}) => {
  const attributes = {};
  
  switch (type) {
    case 'button':
      attributes['role'] = 'button';
      attributes['tabIndex'] = options.disabled ? -1 : 0;
      if (options.pressed !== undefined) {
        attributes['aria-pressed'] = options.pressed;
      }
      if (options.expanded !== undefined) {
        attributes['aria-expanded'] = options.expanded;
      }
      break;
      
    case 'tab':
      attributes['role'] = 'tab';
      attributes['tabIndex'] = options.selected ? 0 : -1;
      attributes['aria-selected'] = options.selected || false;
      if (options.controls) {
        attributes['aria-controls'] = options.controls;
      }
      break;
      
    case 'tabpanel':
      attributes['role'] = 'tabpanel';
      attributes['tabIndex'] = 0;
      if (options.labelledBy) {
        attributes['aria-labelledby'] = options.labelledBy;
      }
      break;
      
    case 'dialog':
      attributes['role'] = 'dialog';
      attributes['aria-modal'] = true;
      if (options.labelledBy) {
        attributes['aria-labelledby'] = options.labelledBy;
      }
      if (options.describedBy) {
        attributes['aria-describedby'] = options.describedBy;
      }
      break;
      
    case 'listbox':
      attributes['role'] = 'listbox';
      if (options.multiselectable) {
        attributes['aria-multiselectable'] = true;
      }
      break;
      
    case 'option':
      attributes['role'] = 'option';
      attributes['aria-selected'] = options.selected || false;
      break;
  }
  
  // Common attributes
  if (options.label) {
    attributes['aria-label'] = options.label;
  }
  if (options.describedBy) {
    attributes['aria-describedby'] = options.describedBy;
  }
  if (options.disabled) {
    attributes['aria-disabled'] = true;
  }
  
  return attributes;
};

/**
 * Create accessible form field attributes
 */
export const createFormFieldAttributes = (field) => {
  const attributes = {
    id: field.id,
    name: field.name,
    'aria-required': field.required || false
  };
  
  if (field.error) {
    attributes['aria-invalid'] = true;
    attributes['aria-describedby'] = `${field.id}-error`;
  }
  
  if (field.description) {
    const describedBy = attributes['aria-describedby'] 
      ? `${attributes['aria-describedby']} ${field.id}-description`
      : `${field.id}-description`;
    attributes['aria-describedby'] = describedBy;
  }
  
  return attributes;
};

// ============================================================================
// KEYBOARD NAVIGATION UTILITIES
// ============================================================================

/**
 * Handle keyboard navigation for lists and grids
 */
export const createKeyboardNavigationHandler = (elements, options = {}) => {
  const {
    orientation = 'vertical', // 'vertical', 'horizontal', 'grid'
    wrap = true,
    activateOnFocus = false
  } = options;
  
  return (event) => {
    const currentIndex = elements.findIndex(el => el === document.activeElement);
    if (currentIndex === -1) return;
    
    let nextIndex = currentIndex;
    
    switch (event.key) {
      case 'ArrowDown':
        if (orientation === 'vertical' || orientation === 'grid') {
          event.preventDefault();
          nextIndex = wrap && currentIndex === elements.length - 1 ? 0 : currentIndex + 1;
        }
        break;
        
      case 'ArrowUp':
        if (orientation === 'vertical' || orientation === 'grid') {
          event.preventDefault();
          nextIndex = wrap && currentIndex === 0 ? elements.length - 1 : currentIndex - 1;
        }
        break;
        
      case 'ArrowRight':
        if (orientation === 'horizontal' || orientation === 'grid') {
          event.preventDefault();
          nextIndex = wrap && currentIndex === elements.length - 1 ? 0 : currentIndex + 1;
        }
        break;
        
      case 'ArrowLeft':
        if (orientation === 'horizontal' || orientation === 'grid') {
          event.preventDefault();
          nextIndex = wrap && currentIndex === 0 ? elements.length - 1 : currentIndex - 1;
        }
        break;
        
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
        
      case 'End':
        event.preventDefault();
        nextIndex = elements.length - 1;
        break;
        
      case 'Enter':
      case ' ':
        if (activateOnFocus) {
          event.preventDefault();
          elements[currentIndex].click();
        }
        break;
    }
    
    if (nextIndex !== currentIndex && elements[nextIndex]) {
      elements[nextIndex].focus();
    }
  };
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if element is visible to screen readers
 */
export const isVisibleToScreenReaders = (element) => {
  if (!element) return false;
  
  const style = window.getComputedStyle(element);
  
  return !(
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    element.hasAttribute('aria-hidden') ||
    element.hasAttribute('hidden') ||
    style.opacity === '0'
  );
};

/**
 * Get accessible name for element
 */
export const getAccessibleName = (element) => {
  if (!element) return '';
  
  // Check aria-label first
  if (element.hasAttribute('aria-label')) {
    return element.getAttribute('aria-label');
  }
  
  // Check aria-labelledby
  if (element.hasAttribute('aria-labelledby')) {
    const labelIds = element.getAttribute('aria-labelledby').split(' ');
    const labels = labelIds.map(id => {
      const labelElement = document.getElementById(id);
      return labelElement ? labelElement.textContent.trim() : '';
    }).filter(Boolean);
    return labels.join(' ');
  }
  
  // Check associated label
  if (element.id) {
    const label = document.querySelector(`label[for="${element.id}"]`);
    if (label) {
      return label.textContent.trim();
    }
  }
  
  // Check title attribute
  if (element.hasAttribute('title')) {
    return element.getAttribute('title');
  }
  
  // Check text content for buttons and links
  if (['BUTTON', 'A'].includes(element.tagName)) {
    return element.textContent.trim();
  }
  
  // Check placeholder for inputs
  if (element.hasAttribute('placeholder')) {
    return element.getAttribute('placeholder');
  }
  
  return '';
};

/**
 * Create accessible form field attributes
 * @param {Object} options - Configuration options
 * @returns {Object} Accessibility attributes
 */
export const createAccessibleFormFieldAttributes = (options = {}) => {
  const { label, description, required = false, invalid = false, errorMessage } = options;
  
  const attributes = {
    'aria-label': label,
    'aria-required': required,
    'aria-invalid': invalid
  };
  
  if (description) {
    attributes['aria-describedby'] = `${options.id || 'field'}-description`;
  }
  
  if (invalid && errorMessage) {
    attributes['aria-describedby'] = `${options.id || 'field'}-error`;
  }
  
  return attributes;
};

/**
 * Create accessible live region for dynamic content
 * @param {string} message - Message to announce
 * @param {string} priority - 'polite' or 'assertive'
 * @returns {Object} Live region attributes
 */
export const createAccessibleLiveRegion = (message, priority = 'polite') => {
  return {
    'aria-live': priority,
    'aria-atomic': 'true',
    role: 'status',
    children: message
  };
};

/**
 * Generate data update announcement for screen readers
 * @param {string} dataType - Type of data updated
 * @param {number} count - Number of items
 * @returns {string} Announcement text
 */
export const generateDataUpdateAnnouncement = (dataType, count) => {
  return `${dataType} updated. ${count} items now available.`;
};

/**
 * Create accessible progress attributes
 * @param {number} value - Current value
 * @param {number} max - Maximum value
 * @param {string} label - Progress label
 * @returns {Object} Progress attributes
 */
export const createAccessibleProgressAttributes = (value, max, label) => {
  return {
    role: 'progressbar',
    'aria-valuenow': value,
    'aria-valuemax': max,
    'aria-valuemin': 0,
    'aria-label': label
  };
};

/**
 * Create accessible tab attributes
 * @param {string} tabId - Tab ID
 * @param {boolean} selected - Whether tab is selected
 * @param {string} panelId - Associated panel ID
 * @returns {Object} Tab attributes
 */
export const createAccessibleTabAttributes = (tabId, selected, panelId) => {
  return {
    id: tabId,
    role: 'tab',
    'aria-selected': selected,
    'aria-controls': panelId,
    tabIndex: selected ? 0 : -1
  };
};

/**
 * Create accessible tab panel attributes
 * @param {string} tabId - Tab ID
 * @param {boolean} selected - Whether tab is selected
 * @returns {Object} Tab panel attributes
 */
export const createAccessibleTabPanelAttributes = (tabId, selected) => {
  return {
    role: 'tabpanel',
    'aria-labelledby': tabId,
    hidden: !selected,
    tabIndex: selected ? 0 : -1
  };
};

// ============================================================================
// INITIALIZATION HELPER
// ============================================================================

/**
 * Initialize baseline accessibility helpers for the app.
 * - Ensures polite and assertive live regions exist (without announcing)
 * - Injects a "Skip to main content" link if missing
 * - Guards against SSR/non-DOM environments
 */
export const initializeAccessibility = () => {
  try {
    if (typeof document === 'undefined') return;

    // Ensure screen reader live regions exist
    ['polite', 'assertive'].forEach((priority) => {
      let region = document.getElementById(`live-region-${priority}`);
      if (!region) {
        region = document.createElement('div');
        region.id = `live-region-${priority}`;
        region.setAttribute('aria-live', priority);
        region.setAttribute('aria-atomic', 'true');
        region.className = 'sr-only';
        region.style.cssText = `
          position: absolute !important;
          width: 1px !important;
          height: 1px !important;
          padding: 0 !important;
          margin: -1px !important;
          overflow: hidden !important;
          clip: rect(0, 0, 0, 0) !important;
          white-space: nowrap !important;
          border: 0 !important;
        `;
        document.body.appendChild(region);
      }
    });

    // Inject a skip link if none exists
    if (!document.getElementById('skip-to-content')) {
      const link = createSkipLink('main-content');
      link.id = 'skip-to-content';
      document.body.prepend(link);
    }

    // Non-intrusive log to aid testing and debugging
    // (Matches expectations from existing tests/util usage)
    // eslint-disable-next-line no-console
    console.log('Accessibility features initialized');
  } catch (_) {
    // Swallow errors to avoid breaking app initialization
  }
};

// Export all utilities
export default {
  // Screen Reader
  announceToScreenReader,
  announceLoadingProgress,
  announceFormErrors,
  announceNavigation,
  
  // Focus Management
  getFocusRingColors,
  getFocusClasses,
  FocusTrap,
  moveFocusTo,
  createSkipLink,
  
  // Contrast and Color
  meetsContrastRequirements,
  getAccessibleColorPairs,
  
  // ARIA
  generateAriaAttributes,
  createFormFieldAttributes,
  
  // Keyboard Navigation
  createKeyboardNavigationHandler,
  
  // Form and UI Accessibility
  createAccessibleFormFieldAttributes,
  createAccessibleLiveRegion,
  generateDataUpdateAnnouncement,
  createAccessibleProgressAttributes,
  createAccessibleTabAttributes,
  createAccessibleTabPanelAttributes,

  // Utilities
  isVisibleToScreenReaders,
  getAccessibleName,

  // Initialization
  initializeAccessibility
};
